<template>
  <div>
    <div class="panel">
      <h3>Two-factor authentication</h3>
      <toggle-control
        v-model="authSettings['require_2fa']"
        label="Require two-factor authentication"
        description="Users will have to provide a code when they authenticate"
      />
      <button class="btn btn--primary" @click="save2FA">
        Save
      </button>
    </div>

    <div class="panel">
      <h3>Authentication provider settings</h3>

      <form @submit.prevent="saveAuthProvider">
        <div class="form-group">
          <label for="slug">
            Slug
          </label>
          <input
            id="slug"
            v-model="authSettings.slug"
            type="text"
            class="form-control form-control--large"
            placeholder="acme"
            minlength="3"
            maxlength="60"
            required
            pattern="[a-z0-9-]+"
          >
          <span class="help-block">
            The slug you pick is used for your personalized login url. E.g.: acme will become /login/<strong>acme</strong>. It should only contain lowercase letters, numbers, and dashes (maximum 60 characters).
          </span>
        </div>

        <div class="form-group">
          <label for="default_role">
            Default role
          </label>
          <select
            id="default_role"
            v-model="authSettings['default_role']"
            class="form-control form-control--small"
            type="text"
          >
            <option value="user">
              User
            </option>
            <option value="admin">
              Admin
            </option>
          </select>
        </div>

        <toggle-control
          v-model="authSettings['default_login']"
          label="Allow basic login with email and password"
          description="Users will be able to login without SSO"
        />

        <button type="submit" class="btn btn--primary">
          Save
        </button>
      </form>

      <h4>Providers</h4>
      <div class="form-group">
        <div class="u-flex-row u-space-between u-align-center">
          <div class="u-flex-row u-align-center">
            <img
              alt="Okta logo"
              srcset="/img/auth-providers/okta@1x.png, /img/auth-providers/okta@2x.png 2x"
              src="/img/auth-providers/okta@1x.png"
              class="u-mr-medium"
            >
            <h5 class="u-mv-none">
              Okta
            </h5>
          </div>
          <div class="u-flex-row u-align-center">
            <div v-if="authSettings['current_provider'] === 'okta'" class="tag tag-light active tag--success">
              Active
            </div>
            <a class="btn u-ml-medium" :href="loginUrl ? '/account/auth/okta' : '#'" :disabled="!loginUrl">
              Configure Okta
            </a>
          </div>
        </div>
      </div>

      <div v-if="!loginUrl" class="alert alert--info u-mb-none">
        <p>
          Please provide a slug and save to configure an authentication provider.
        </p>
      </div>

      <div v-if="authSettings['current_provider']" class="alert alert--info u-mb-none u-mt-x-large">
        <p><strong>Login URL</strong></p>
        <p>
          Your personalized login URL is
          <a class="emphasis-link" :href="fullLoginUrl">{{ fullLoginUrl }}</a>.
          Members of your organization should access Blendr.io via this url.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { axios } from '@qlik-trial/automations-ui'
import { MessageLevel } from '@qlik-trial/automations-ui'

export default {
  props: {
    initialAuthSettings: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      authSettings: cloneDeep(this.initialAuthSettings),
      loginUrl: this.initialAuthSettings.slug,
    }
  },

  computed: {
    fullLoginUrl() {
      return window.location.origin + '/login/' + this.loginUrl
    },
  },

  methods: {
    save2FA() {
      axios.post('/account/auth', this.authSettings)
        .then(response => {
          const enabled = response.data['require_2fa']
          this.flash({
            message: `Two-factor authentication was ${ enabled ? 'enabled' : 'disabled'}`,
            level: MessageLevel.SUCCESS,
          })
        })
        .catch(_error => {
          const enabling = this.authSettings['require_2fa']
          this.flash({
            message: `An error occurred ${ enabling ? 'enabling' : 'disabling' } Two-Factor Authentication`,
            level: MessageLevel.DANGER,
          })

          this.reset()
        })
    },

    saveAuthProvider() {
      axios.post('/account/auth', this.authSettings)
        .then(_response => {
          this.flash({
            message: 'Authentication provider settings were saved',
            level: MessageLevel.SUCCESS,
          })
          this.loginUrl = this.authSettings.slug
        })
        .catch(error => {
          console.log(error)
          this.flash({
            message: 'An error occurred saving Authentication Provider Settings',
            level: MessageLevel.DANGER,
          })
          this.reset()
        })
    },

    reset() {
      this.authSettings = cloneDeep(this.initialAuthSettings)
    },
  },
}
</script>
